import React, { useRef, useState } from 'react';
import { Button } from './ui/button';
import {
  CheckCircle2, MessageCircle, CalendarCheck, Copy, Check, Sparkles,
  AlertTriangle, ChevronDown, ChevronUp, ClipboardList, Info, Wrench,
  Eye, EyeOff, ShieldQuestion,
} from 'lucide-react';

// ---- Mobile before/after slider ----
const SwipeSlider = ({ before, after }) => {
  const [pos, setPos] = useState(50);
  const wrapRef = useRef(null);
  const move = (clientX) => {
    if (!wrapRef.current) return;
    const rect = wrapRef.current.getBoundingClientRect();
    const p = ((clientX - rect.left) / rect.width) * 100;
    setPos(Math.max(0, Math.min(100, p)));
  };
  return (
    <div
      ref={wrapRef}
      className="relative w-full overflow-hidden rounded-2xl shadow-2xl select-none touch-none"
      onMouseMove={(e) => e.buttons === 1 && move(e.clientX)}
      onTouchMove={(e) => move(e.touches[0].clientX)}
      data-testid="estimator-swipe-slider"
    >
      <img src={before} alt="Original room" className="w-full block" />
      <div className="absolute inset-0 overflow-hidden" style={{ width: `${pos}%` }}>
        <img src={after} alt="With AC unit" className="block h-full w-auto max-w-none" style={{ width: wrapRef.current?.offsetWidth || '100%' }} />
      </div>
      <div className="absolute top-0 bottom-0 w-1 bg-[#FF8C00] cursor-ew-resize" style={{ left: `${pos}%`, transform: 'translateX(-50%)' }}>
        <div className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 left-1/2 w-10 h-10 rounded-full bg-[#FF8C00] grid place-items-center text-white text-xs font-bold shadow-lg">⇆</div>
      </div>
      <span className="absolute top-3 left-3 text-[11px] uppercase tracking-wider bg-black/60 text-white px-2 py-1 rounded">Before</span>
      <span className="absolute top-3 right-3 text-[11px] uppercase tracking-wider bg-[#FF8C00] text-white px-2 py-1 rounded">After</span>
    </div>
  );
};

// ---- Placement confidence panel ----
const PlacementPanel = ({ placement, onBookSurvey }) => {
  if (!placement) return null;
  const band = placement.confidenceBand;
  const state = placement.state;
  const conf = Math.round((placement.confidence || 0) * 100);

  if (state === 'refused') {
    return (
      <div className="bg-gradient-to-br from-amber-500/10 to-amber-500/5 border-2 border-amber-500/50 rounded-2xl p-6 sm:p-8 mb-8" data-testid="placement-refused">
        <div className="flex items-start gap-4 mb-5">
          <div className="w-12 h-12 rounded-full bg-amber-500/15 grid place-items-center flex-shrink-0">
            <EyeOff size={22} className="text-amber-400" />
          </div>
          <div className="flex-1">
            <p className="text-[10px] uppercase tracking-[0.25em] text-amber-400 mb-1">AI Placement Analysis</p>
            <h3 className="text-xl sm:text-2xl font-bold mb-2">No clear mounting wall identified</h3>
            <p className="text-sm text-gray-300 leading-relaxed">
              Our placement engine couldn't find an installer-grade wall in this photo. A graceful
              refusal is more honest than fake certainty — here's what the AI saw:
            </p>
          </div>
        </div>
        {!!placement.reasons?.length && (
          <ul className="bg-black/25 rounded-xl p-4 mb-5 space-y-2" data-testid="placement-reasons">
            {placement.reasons.map((r, i) => (
              <li key={i} className="text-sm text-gray-200 flex items-start gap-2">
                <span className="text-amber-400 mt-1">•</span> {r}
              </li>
            ))}
          </ul>
        )}
        <button
          type="button"
          onClick={onBookSurvey}
          className="w-full sm:w-auto bg-amber-500 hover:bg-amber-600 text-black font-bold px-6 py-3 rounded-lg transition"
          data-testid="placement-refused-cta"
        >
          Book a technical site survey →
        </button>
      </div>
    );
  }

  const bandStyles = {
    high:   { border: 'border-emerald-500/40', bg: 'bg-emerald-500/[0.06]', icon: 'text-emerald-400', text: 'High confidence' },
    medium: { border: 'border-[#FF8C00]/40',   bg: 'bg-[#FF8C00]/[0.06]',   icon: 'text-[#FF8C00]',    text: 'Medium confidence' },
    low:    { border: 'border-amber-500/50',   bg: 'bg-amber-500/[0.08]',   icon: 'text-amber-400',    text: 'Low confidence — survey advised' },
  };
  const s = bandStyles[band] || bandStyles.medium;

  return (
    <div className={`border ${s.border} ${s.bg} rounded-2xl p-5 mb-8`} data-testid="placement-confidence-banner">
      <div className="flex items-start gap-4">
        <div className="w-10 h-10 rounded-full bg-white/5 grid place-items-center flex-shrink-0">
          <Eye size={18} className={s.icon} />
        </div>
        <div className="flex-1">
          <div className="flex flex-wrap items-center gap-2 mb-1">
            <p className="text-[10px] uppercase tracking-[0.22em] text-gray-400">AI Placement</p>
            <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-white/10 ${s.icon} font-bold`} data-testid="placement-band-label">
              {s.text} · {conf}%
            </span>
          </div>
          {placement.wallDescription && (
            <p className="font-bold text-sm mb-1" data-testid="placement-wall-description">Best candidate: {placement.wallDescription}</p>
          )}
          {band !== 'high' && !!placement.reasons?.length && (
            <ul className="mt-2 space-y-1">
              {placement.reasons.slice(0, 3).map((r, i) => (
                <li key={i} className="text-xs text-gray-400 flex items-start gap-2">
                  <span className="text-gray-500 mt-0.5">•</span>{r}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};


// ---- Install type banner ----
const InstallTypeBanner = ({ installType }) => {
  if (!installType?.label) return null;
  return (
    <div className="mb-6 bg-[#121820]/70 border border-[#FF8C00]/25 rounded-2xl p-5 flex items-start gap-4" data-testid="install-type-banner">
      <div className="w-10 h-10 rounded-full bg-[#FF8C00]/15 grid place-items-center flex-shrink-0">
        <Wrench size={18} className="text-[#FF8C00]" />
      </div>
      <div className="flex-1">
        <p className="text-[10px] uppercase tracking-[0.25em] text-[#FF8C00]/85 mb-1">Likely installation type</p>
        <p className="font-bold text-base mb-1" data-testid="install-type-label">{installType.label}</p>
        {installType.description && (
          <p className="text-xs text-gray-400 leading-relaxed">{installType.description}</p>
        )}
      </div>
    </div>
  );
};

// ---- Price card by quote mode ----
const PriceCard = ({ pricing }) => {
  const mode = pricing.quoteMode || 'fixed';
  const vatBadge = (
    <p className="text-xs uppercase tracking-[0.2em] text-[#FF8C00]/90 mb-5" data-testid="estimator-vat-badge">
      {pricing.alreadyOwn ? 'Labour-only · VAT included' : 'VAT included'}
    </p>
  );
  const meta = (
    <div className="flex flex-wrap justify-center gap-3 text-sm mt-2">
      <div className="bg-white/10 px-4 py-2 rounded-lg" data-testid="meta-unit"><CheckCircle2 size={16} className="inline mr-2 text-[#FF8C00]" />{pricing.recommendedUnit}</div>
      {!!pricing.estimatedArea && <div className="bg-white/10 px-4 py-2 rounded-lg" data-testid="meta-area"><CheckCircle2 size={16} className="inline mr-2 text-[#FF8C00]" />~{Math.round(pricing.estimatedArea)}m²</div>}
      <div className="bg-white/10 px-4 py-2 rounded-lg" data-testid="meta-brand"><CheckCircle2 size={16} className="inline mr-2 text-[#FF8C00]" />{pricing.brandLabel}</div>
    </div>
  );

  if (mode === 'survey') {
    const isAlreadyOwn = pricing.surveyReason === 'already-own-equipment' || pricing.alreadyOwn;
    const isCommercial = pricing.surveyReason === 'commercial-property';
    const headline = isAlreadyOwn
      ? 'Site survey required'
      : isCommercial
        ? 'Commercial site survey recommended'
        : 'Site survey recommended';
    const subcopy = isAlreadyOwn
      ? "Customer-supplied equipment introduces variables we won't auto-quote online — capacity, brackets, missing parts, no hardware warranty. A short site survey locks in a fixed labour-only price on the day."
      : isCommercial
        ? 'Commercial properties carry compliance and design overheads we need to verify on-site before fixing a price.'
        : "This installation has factors that materially affect price and need on-site verification. We won't auto-quote because under-quoting wastes everyone's time.";
    return (
      <div className="bg-gradient-to-br from-amber-500/15 to-amber-500/5 border-2 border-amber-500/60 rounded-2xl p-8 mb-8" data-testid="estimator-price-card-survey">
        <div className="flex items-start gap-4 mb-5">
          <div className="w-14 h-14 rounded-full bg-amber-500/15 grid place-items-center flex-shrink-0">
            <AlertTriangle size={28} className="text-amber-400" />
          </div>
          <div>
            <p className="text-xs uppercase tracking-[0.25em] text-amber-400 mb-1">Pre-survey assessment</p>
            <h3 className="text-2xl sm:text-3xl font-extrabold">{headline}</h3>
            <p className="text-sm text-gray-300 mt-2 leading-relaxed">{subcopy}</p>
          </div>
        </div>
        {!isAlreadyOwn && !!pricing.complexityFactors?.length && (
          <div className="bg-black/30 rounded-xl p-4 mb-5" data-testid="estimator-complexity-factors">
            <p className="text-xs uppercase tracking-[0.2em] text-gray-400 mb-3">Why survey recommended</p>
            <ul className="space-y-2">
              {pricing.complexityFactors.map((f, i) => (
                <li key={i} className="text-sm text-gray-200 flex items-start gap-2">
                  <span className="text-amber-400 mt-1">•</span> {f}
                </li>
              ))}
            </ul>
          </div>
        )}
        {pricing.totalPrice ? (
          <div className="text-center">
            <p className="text-xs uppercase tracking-[0.2em] text-gray-400 mb-1">Indicative figure — final price set after survey</p>
            <p className="text-3xl sm:text-4xl font-extrabold text-amber-400">£{pricing.totalPrice.toLocaleString()}</p>
            <p className="text-xs text-gray-400 mt-1">{pricing.brandLabel} · VAT included</p>
          </div>
        ) : (
          <div className="text-center">
            <p className="text-sm text-gray-300">Book a site survey to lock in your fixed price.</p>
          </div>
        )}
      </div>
    );
  }

  // Fixed (matrix-driven, no range subtext)
  return (
    <div className="bg-gradient-to-br from-[#FF8C00]/20 to-[#FF8C00]/5 border-2 border-[#FF8C00]/50 rounded-2xl p-8 mb-8 text-center" data-testid="estimator-price-card">
      <p className="text-sm uppercase tracking-[0.25em] text-gray-300 mb-2">Total Installation Price</p>
      <p className="text-6xl sm:text-7xl font-extrabold text-[#FF8C00] mb-2">£{pricing.totalPrice.toLocaleString()}</p>
      <p className="text-sm text-gray-400 mb-3">All-inclusive: hardware, materials, F-Gas labour &amp; VAT</p>
      {vatBadge}
      {meta}
    </div>
  );
};

// ---- "Why this estimate?" panel ----
const PriceExplanation = ({ pricing }) => {
  const [open, setOpen] = useState(true);
  const bullets = pricing.priceExplanation || [];
  if (!bullets.length) return null;
  return (
    <div className="bg-[#121820]/80 backdrop-blur-sm border border-white/10 rounded-2xl mb-8 overflow-hidden" data-testid="estimator-price-explanation">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full px-6 py-4 flex items-center justify-between hover:bg-white/[0.02] transition"
        data-testid="estimator-explanation-toggle"
      >
        <span className="flex items-center gap-3 font-medium">
          <Info size={20} className="text-[#FF8C00]" /> Why this estimate?
        </span>
        {open ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
      </button>
      {open && (
        <ul className="px-6 pb-6 space-y-2.5">
          {bullets.map((b, i) => (
            <li key={i} className="text-sm text-gray-200 flex items-start gap-3" data-testid={`explanation-bullet-${i}`}>
              <span className="text-[#FF8C00] mt-1">•</span>
              <span className="flex-1 leading-relaxed">{b}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

// ---- What's included accordion ----
const PriceBreakdown = ({ pricing }) => {
  const [open, setOpen] = useState(false);
  if (!pricing.breakdown) return null;
  const rows = [
    ['Equipment & install (matrix base)', pricing.breakdown.baseEquipmentAndInstall],
    ['Height-access surcharge',           pricing.breakdown.floorLevel],
    ['Outdoor placement',                 pricing.breakdown.outdoorPlacement],
    ['Pipe routing',                      pricing.breakdown.pipeRouting],
    ['Dedicated electrical feed',         pricing.breakdown.dedicatedElectricalFeed],
    ['Condensate drain pump',             pricing.breakdown.condensateDrainPump],
    ['Reuse existing pipework credit',    pricing.breakdown.reuseExistingPipework],
  ].filter(([, v]) => v !== 0 && v != null);
  return (
    <div className="bg-[#121820]/80 backdrop-blur-sm border border-white/10 rounded-2xl mb-8 overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full px-6 py-4 flex items-center justify-between hover:bg-white/[0.02] transition"
        data-testid="estimator-breakdown-toggle"
      >
        <span className="flex items-center gap-3 font-medium">
          <ClipboardList size={20} className="text-[#FF8C00]" /> What's included in this quote
        </span>
        {open ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
      </button>
      {open && (
        <div className="px-6 pb-6">
          <ul className="divide-y divide-white/5">
            {rows.map(([label, val]) => (
              <li key={label} className="flex justify-between py-3 text-sm">
                <span className="text-gray-400">{label}</span>
                <span className={`font-medium ${val < 0 ? 'text-emerald-400' : 'text-gray-100'}`}>
                  {val < 0 ? '−' : '+'}£{Math.abs(val).toLocaleString()}
                </span>
              </li>
            ))}
            <li className="flex justify-between py-3 text-base font-bold border-t border-white/10 mt-2">
              <span>Total (inc VAT)</span>
              <span className="text-[#FF8C00]">£{pricing.totalPrice.toLocaleString()}</span>
            </li>
          </ul>
        </div>
      )}
    </div>
  );
};

export const QuoteResults = ({
  pricing, narrative, rooms, outdoor,
  // Legacy single-room view kept for backwards compatibility with /q/<id> shared pages
  imageOriginalUrl, imageVisualizedUrl, placement,
  // Multi-room shared view passes these instead
  roomImageBaseUrl,  // function(idx, type) returning URL — see QuoteView
  outdoorImageUrl,
  mockMode, shareUrl, isSharedView,
  onWhatsApp, onBookSurvey, onReset, onCopyLink,
}) => {
  const [copied, setCopied] = useState(false);
  const isSurvey = pricing.quoteMode === 'survey';

  // Normalise multi-room data — accepts either inline `rooms[]` (from estimator response, with data: URIs)
  // or `roomImageBaseUrl` callback (from QuoteView shared page).
  const normalizedRooms = rooms || [];
  const firstRoomPlacement = normalizedRooms[0]?.placement || placement;

  const getRoomOriginal = (idx) => normalizedRooms[idx]?.originalImage || (roomImageBaseUrl && roomImageBaseUrl(idx, 'image'));
  const getRoomVisualized = (idx) => {
    const r = normalizedRooms[idx];
    if (!r) return null;
    if (r.visualizedImage) return r.visualizedImage; // inline data URI from estimator response
    if (r.hasVisualization && roomImageBaseUrl) return roomImageBaseUrl(idx, 'visualized');
    return null;
  };

  const handleCopy = async () => {
    try { await navigator.clipboard.writeText(shareUrl); }
    catch (e) {
      const ta = document.createElement('textarea');
      ta.value = shareUrl; document.body.appendChild(ta); ta.select();
      document.execCommand('copy'); document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2200);
    onCopyLink?.();
  };

  return (
    <div data-testid="quote-results">
      {isSharedView && (
        <div className="mb-8 bg-[#FF8C00]/10 border border-[#FF8C00]/30 rounded-2xl p-5 sm:p-6 flex flex-col sm:flex-row items-center gap-4" data-testid="shared-banner">
          <div className="w-12 h-12 rounded-full bg-[#FF8C00]/20 grid place-items-center flex-shrink-0">
            <Sparkles size={22} className="text-[#FF8C00]" />
          </div>
          <div className="flex-1 text-center sm:text-left">
            <p className="text-xs uppercase tracking-[0.25em] text-[#FF8C00] mb-1">Shared AI installation estimate</p>
            <p className="text-sm text-gray-300">Generated by Deep Control's AI pre-survey engine. Try it free with your own room photo.</p>
          </div>
          <Button onClick={() => (window.location.href = '/ai-estimator')} className="bg-[#FF8C00] hover:bg-[#e67e00] text-white font-bold whitespace-nowrap" data-testid="shared-banner-cta">
            Get your own estimate
          </Button>
        </div>
      )}

      {mockMode && (
        <div className="mb-8 text-center">
          <span className="text-xs uppercase tracking-[0.25em] text-[#FF8C00]/80 bg-[#FF8C00]/5 border border-[#FF8C00]/20 rounded-full px-4 py-1.5" data-testid="estimator-mock-banner">
            Preview mode — Gemini Vision not yet configured
          </span>
        </div>
      )}

      <h2 className="text-4xl sm:text-5xl font-extrabold text-center mb-3">
        {isSurvey ? 'Pre-Survey Assessment' : (isSharedView ? "Here's the AI Quote" : 'Your Installation Estimate')}
      </h2>
      <p className="text-center text-gray-400 mb-10">
        {isSurvey
          ? 'Our pre-survey engine has flagged this job for an on-site review.'
          : 'Engineered by AI. Pre-survey grade.'}
      </p>

      <InstallTypeBanner installType={pricing.installType} />

      <PlacementPanel placement={firstRoomPlacement} onBookSurvey={onBookSurvey} />


      <PriceCard pricing={pricing} />

      <PriceExplanation pricing={pricing} />

      {!isSurvey && <PriceBreakdown pricing={pricing} />}

      {!!narrative && (
        <div className="bg-[#121820]/60 border border-white/10 rounded-2xl p-5 mb-8">
          <p className="text-sm text-gray-300 leading-relaxed">{narrative}</p>
        </div>
      )}

      {/* Multi-room visualisation gallery */}
      {normalizedRooms.length > 0 && (
        <div className="mb-10" data-testid="rooms-gallery">
          <p className="text-center text-xs uppercase tracking-[0.25em] text-gray-400 mb-5">
            {normalizedRooms.length === 1 ? 'See it on your wall' : `AI visualisation · ${normalizedRooms.length} rooms`}
          </p>
          <div className="space-y-8">
            {normalizedRooms.map((room, idx) => {
              const orig = getRoomOriginal(idx);
              const viz = getRoomVisualized(idx);
              const p = room.placement || {};
              return (
                <div key={idx} className="bg-[#0d1620]/60 border border-white/10 rounded-2xl p-4 sm:p-5" data-testid={`room-card-${idx}`}>
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="text-lg font-bold">{room.label || `Room ${idx + 1}`}</h4>
                    {p.confidenceBand && (
                      <span className={`text-[10px] uppercase tracking-wider px-2.5 py-1 rounded-full ${
                        p.confidenceBand === 'high' ? 'bg-emerald-500/15 text-emerald-300' :
                        p.confidenceBand === 'medium' ? 'bg-amber-500/15 text-amber-300' :
                        'bg-gray-500/15 text-gray-300'
                      }`}>
                        AI {p.confidenceBand}-confidence
                      </span>
                    )}
                  </div>
                  {viz ? (
                    <>
                      <div className="hidden md:grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-center text-xs font-bold mb-2 text-gray-400">Original</p>
                          <img src={orig} alt={`${room.label} original`} className="w-full rounded-xl border border-white/10" />
                        </div>
                        <div>
                          <p className="text-center text-xs font-bold mb-2 text-[#FF8C00]">With AC installed</p>
                          <img src={viz} alt={`${room.label} visualized`} className="w-full rounded-xl border border-[#FF8C00]/30" />
                        </div>
                      </div>
                      <div className="md:hidden">
                        <SwipeSlider before={orig} after={viz} />
                        <p className="text-center text-xs text-gray-500 mt-2">Drag to compare</p>
                      </div>
                    </>
                  ) : orig ? (
                    <div>
                      <img src={orig} alt={room.label} className="w-full rounded-xl border border-white/10 max-h-80 object-contain mx-auto" />
                      <div className="mt-3 bg-white/5 border border-white/10 rounded-lg p-3 flex items-start gap-2">
                        <ShieldQuestion size={16} className="text-gray-300 flex-shrink-0 mt-0.5" />
                        <p className="text-xs text-gray-400 leading-relaxed">
                          {p.visualizationStatus === 'unavailable'
                            ? "AI couldn't generate a believable render for this room. Site survey will confirm placement."
                            : 'AI visualisation not attempted for this room — confidence below the photoreal threshold.'}
                        </p>
                      </div>
                    </div>
                  ) : null}
                  {p.wallDescription && (
                    <p className="text-xs text-gray-500 mt-3 italic">AI's chosen wall: {p.wallDescription}</p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Outdoor unit card */}
      {outdoor && (outdoor.analysis || outdoorImageUrl) && (
        <div className="mb-10 bg-[#0d1620]/60 border border-white/10 rounded-2xl p-4 sm:p-5" data-testid="outdoor-card">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-lg font-bold flex items-center gap-2">
              <CalendarCheck size={18} className="text-[#FF8C00]" /> Outdoor condenser location
            </h4>
            {outdoor.analysis?.viable !== undefined && (
              <span className={`text-[10px] uppercase tracking-wider px-2.5 py-1 rounded-full ${
                outdoor.analysis.viable ? 'bg-emerald-500/15 text-emerald-300' : 'bg-amber-500/15 text-amber-300'
              }`}>
                {outdoor.analysis.viable ? 'Viable' : 'Needs review'}
              </span>
            )}
          </div>
          <div className="grid md:grid-cols-2 gap-5">
            {outdoorImageUrl && (
              <img src={outdoorImageUrl} alt="Outdoor placement" className="w-full rounded-xl border border-white/10 max-h-72 object-contain" />
            )}
            <div className="text-sm space-y-2 text-gray-300">
              {outdoor.analysis?.surface && (
                <p><span className="text-gray-500">Mounting surface:</span> {outdoor.analysis.surface}</p>
              )}
              {outdoor.analysis?.estimatedAccess && (
                <p><span className="text-gray-500">Access:</span> {outdoor.analysis.estimatedAccess}</p>
              )}
              {outdoor.analysis?.observations?.length > 0 && (
                <div>
                  <p className="text-gray-500 mb-1">AI installer notes:</p>
                  <ul className="space-y-1">
                    {outdoor.analysis.observations.map((o, i) => (
                      <li key={i} className="flex items-start gap-1.5 text-xs"><span className="text-[#FF8C00] mt-0.5">•</span><span>{o}</span></li>
                    ))}
                  </ul>
                </div>
              )}
              {outdoor.analysis?.concerns?.length > 0 && (
                <div>
                  <p className="text-amber-400 text-xs uppercase tracking-wider mt-3 mb-1">Concerns</p>
                  <ul className="space-y-1">
                    {outdoor.analysis.concerns.map((c, i) => (
                      <li key={i} className="flex items-start gap-1.5 text-xs text-amber-200"><span className="mt-0.5">⚠</span><span>{c}</span></li>
                    ))}
                  </ul>
                </div>
              )}
              {outdoor.analysis?.safetyNotice && (
                <div className="bg-red-500/10 border border-red-500/30 rounded p-2 text-xs text-red-200 mt-2">
                  {outdoor.analysis.safetyNotice}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Legacy single-image view (for old shared quotes that pre-date the multi-room schema) */}
      {normalizedRooms.length === 0 && imageVisualizedUrl && (
        <div className="mb-10">
          <p className="text-center text-xs uppercase tracking-[0.25em] text-gray-400 mb-4">See it on your wall</p>
          <div className="hidden md:grid grid-cols-2 gap-6" data-testid="estimator-before-after-desktop">
            <div>
              <p className="text-center font-bold mb-3 text-gray-300">Original Photo</p>
              <img src={imageOriginalUrl} alt="Original" className="w-full rounded-2xl shadow-2xl border border-white/10" />
            </div>
            <div>
              <p className="text-center font-bold mb-3 text-[#FF8C00]">With AC Unit Installed</p>
              <img src={imageVisualizedUrl} alt="Visualized" className="w-full rounded-2xl shadow-2xl border border-[#FF8C00]/30" />
            </div>
          </div>
          <div className="md:hidden">
            <SwipeSlider before={imageOriginalUrl} after={imageVisualizedUrl} />
            <p className="text-center text-xs text-gray-500 mt-3">Drag the handle to compare</p>
          </div>
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-3 justify-center flex-wrap">
        <Button onClick={onBookSurvey} className="bg-[#FF8C00] hover:bg-[#e67e00] text-white font-bold px-6 py-6" data-testid="estimator-book-cta">
          <CalendarCheck size={20} className="mr-2" /> {isSurvey ? 'Book site survey' : 'Book installation'}
        </Button>
        <Button onClick={onWhatsApp} className="bg-[#25D366] hover:bg-[#1ebd5b] text-white font-bold px-6 py-6" data-testid="estimator-whatsapp-cta">
          <MessageCircle size={20} className="mr-2" /> Send to WhatsApp
        </Button>
        <Button onClick={handleCopy} className="bg-[#121820] border-2 border-white/20 hover:bg-white/5 text-white font-bold px-6 py-6" data-testid="estimator-copy-link-cta">
          {copied ? <Check size={20} className="mr-2 text-[#FF8C00]" /> : <Copy size={20} className="mr-2" />}
          {copied ? 'Link copied!' : 'Copy quote link'}
        </Button>
        {!isSharedView && (
          <Button onClick={onReset} className="bg-transparent border-2 border-white/20 hover:bg-white/10 text-white font-bold px-6 py-6" data-testid="estimator-restart-btn">
            Start new estimate
          </Button>
        )}
      </div>
    </div>
  );
};

export default QuoteResults;
